import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { PhoneCallIcon, StickyNoteIcon, MailIcon, BrainIcon } from 'lucide-react';
const scatterPoints = [{
  icon: PhoneCallIcon,
  label: 'Dozens of calls a week',
  from: {
    x: -40,
    y: 20
  }
}, {
  icon: StickyNoteIcon,
  label: 'Notes in five places',
  from: {
    x: 20,
    y: -36
  }
}, {
  icon: MailIcon,
  label: 'Details buried in email',
  from: {
    x: -20,
    y: -36
  }
}, {
  icon: BrainIcon,
  label: 'Memory only goes so far',
  from: {
    x: 40,
    y: 20
  }
}];
export function Problem() {
  return <section className="relative overflow-hidden border-t border-slate-100 texture-scanlines py-20 lg:py-28" data-id="element-324">
      <span aria-hidden="true" className="font-mono-brand pointer-events-none absolute -right-10 top-1/2 -translate-y-1/2 rotate-90 text-[10rem] font-bold tracking-tighter text-slate-900/[0.04] lg:text-[14rem]" data-id="element-325">
        CAPTURE
      </span>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-326">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="mx-auto max-w-3xl text-center" data-id="element-327">
          <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-328">
            // the_problem
          </p>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl" data-id="element-329">
            You talk to dozens of borrowers. The details don't keep up.
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-600" data-id="element-330">
            What was said. What was promised. What needs to happen next. It
            scatters across calls, sticky notes, email threads, and memory — and
            by the next conversation, you're reconstructing it from scratch.
          </p>
        </motion.div>

        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-331">
          {scatterPoints.map((point, i) => <motion.div key={point.label} initial={{
          opacity: 0,
          x: point.from.x,
          y: point.from.y
        }} whileInView={{
          opacity: 1,
          x: 0,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.55,
          delay: i * 0.08,
          ease: 'easeOut'
        }} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm" data-id="element-332">
              <point.icon className="mx-auto h-6 w-6 text-teal-600" aria-hidden="true" data-id="element-333" />
              <p className="mt-3 text-sm font-medium text-slate-700" data-id="element-334">
                {point.label}
              </p>
            </motion.div>)}
        </div>

        <motion.p initial={{
        opacity: 0,
        scale: 0.96
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.4,
        ease: 'easeOut'
      }} className="font-display mx-auto mt-12 max-w-2xl text-center text-xl font-bold text-ink lg:text-2xl" data-id="element-335">
          Recall turns every conversation into{' '}
          <span className="text-teal-600" data-id="element-336">
            organized, searchable borrower context
          </span>{' '}
          — so nothing slips.
        </motion.p>
      </div>
    </section>;
}